/**
 * Что можно сделать с нехваткой.
 *
 * Экран отвечает на вопрос, который человек задаёт, увидев «не хватает»: не
 * «почему так», а «что мне делать». Отсюда три правила.
 *
 * · **Рычаги настоящие.** Каждый пункт что-то меняет в данных: переводит
 *   деньги, уменьшает конверт. Совета «трать меньше» здесь нет — он ничего не
 *   меняет, а значит и не помогает.
 * · **Сначала последствия, потом действие.** Нажатие на пункт денег не
 *   трогает: открывается предпросмотр — сколько станет свободно и чем за это
 *   платим. Подтверждает человек.
 * · **«Ничего не менять» — тоже ответ.** С ценой в рублях и в день, чтобы это
 *   было решением, а не уходом от разговора.
 *
 * Чего здесь намеренно нет: перекладывания между «Повседневные нужды» и «Свободные траты». При
 * нехватке денег оно не создаёт ни рубля — это разметка одного кошелька, — и
 * создавало бы ощущение, что вопрос закрыт.
 */
import { useState, type CSSProperties } from 'react';
import {
  ZERO, addMonths, type ReserveTarget, clampPos, cycleStartFrom, formatMoney, gapOutlook, goalEta, isAfter, minM, sub,
  type Money, type PlainDate,
} from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { flash } from './flash.js';
import {
  addExpectedIncome, addTransfer, incomeFacts, setReserve, skipGoalThisCycle, todayPd,
  type AppState,
} from './store.js';
import { plural } from './text.js';
import { Icon } from './icon.js';
import { DateButton } from './date-picker.js';

/** Один рычаг: что он даёт и чем за это платят. */
interface Lever {
  id: string;
  title: string;
  hint: string;
  /** Сколько предложить взять по умолчанию. */
  suggest: Money;
  /** Больше этого рычаг не даёт. */
  max: Money;
  /** Цена решения — словами. Показывается до подтверждения. */
  cost: string;
  /**
   * Что делает кнопка — своими словами. Одно «Взять» прятало разницу между
   * настоящим переводом и снятием назначения с уже лежащих денег.
   */
  cta: (amount: Money) => string;
  /** Группа в списке: накопления, конверты или взносы в цели. */
  group: 'own' | 'envelope' | 'goal';
  /** В списке — коротко: имя под заголовком группы и одна подпись с суммой. */
  name: string;
  sub: string;
  /**
   * Сумму выбирает не человек.
   *
   * Пропуск взноса освобождает ровно взнос: «пропустить половину» нечем
   * записать — цель либо получила деньги за этот приход, либо нет. Поле ввода
   * здесь обещало бы выбор, которого нет.
   */
  fixed?: boolean;
  apply: (amount: Money) => Promise<void>;
}

const NOTHING = 'nothing';
const AWAIT = 'await';

export function RemedySheet({
  state, gap, available, daysLeft, needBy, holds, exclude, onPlanIncome, onClose, onDone,
}: {
  state: AppState;
  /** Сколько не хватает. */
  gap: Money;
  /** Сколько свободно сейчас. */
  available: Money;
  /** Дней до следующего прихода — для цены бездействия. */
  daysLeft: number;
  /** К какому дню деньги нужны: следующий плановый приход. */
  needBy: PlainDate;
  /**
   * Взносы в цели, которые сейчас вычтены из свободных денег.
   *
   * Приходят готовыми из расчёта, а не считаются здесь заново: вычет уже знает
   * про частичные конверты и про то, что цель «закрыть кредитку» не просит
   * второй раз денег, вычтенных под платёж по карте.
   */
  holds: readonly { id: string; label: string; amount: Money }[];
  /**
   * Чья это нехватка. Конверт под тот же платёж в вариантах не предлагается:
   * «взять из конверта «Т-Платинум»», чтобы закрыть нехватку «Т-Платинум», — круг.
   */
  exclude?: { kind: ReserveTarget; id: string };
  /** Назначить на этот платёж будущий приход — зарплату, аванс, ожидание. */
  onPlanIncome?: () => void;
  onClose: () => void;
  onDone: () => void;
}) {
  const [picked, setPicked] = useState<Lever | typeof NOTHING | typeof AWAIT | null>(null);
  const [raw, setRaw] = useState('');
  const [when, setWhen] = useState('');
  const [what, setWhat] = useState('');
  const [checkpoint, setCheckpoint] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Длинные группы свёрнуты до трёх самых крупных: список читался простынёй (03.11.2026).
  const [opened, setOpened] = useState<Record<string, boolean>>({});

  const liquid = state.accounts.filter(
    (a) => !a.archived && a.isLiquid && a.kind !== 'credit_card',
  );
  const into = liquid[0];
  const savings = state.accounts.filter(
    (a) => !a.archived && !a.isLiquid && a.balance > ZERO && a.kind !== 'credit_card',
  ).sort((a, b) => b.balance - a.balance);

  const levers: Lever[] = [];

  // 1. Перевод с накопительного — единственный рычаг, дающий живые деньги.
  for (const acc of savings) {
    levers.push({
      id: `transfer:${acc.id}`,
      title: `Перевести с «${acc.name}»`,
      hint: `Там ${formatMoney(acc.balance)}, и в свободные деньги они не входят`,
      suggest: minM(acc.balance, gap > ZERO ? gap : acc.balance),
      max: acc.balance,
      cost: `Перевод с «${acc.name}» на «${into?.name ?? 'счёт для трат'}»: остаток «${acc.name}» уменьшится на эту сумму`,
      cta: (v) => `Перевести ${formatMoney(v)} на «${into?.name ?? 'счёт'}»`,
      group: 'own',
      name: acc.name,
      sub: `есть ${formatMoney(acc.balance)}`,
      apply: async (v) => {
        if (!into) throw new Error('Нет счёта для трат, на который перевести');
        await addTransfer({
          fromId: acc.id, toId: into.id, amount: v, date: todayPd(),
          note: 'Из накоплений',
        });
      },
    });
  }

  // 2. Конверт: деньги есть, но обещаны конкретному требованию. Освободить их
  //    можно — но тогда требование остаётся без покрытия, и это надо сказать.
  const envelopes = state.reserves
    .filter((x) => x.amount > ZERO && !(exclude && x.forKind === exclude.kind && x.forId === exclude.id))
    .sort((a, b) => b.amount - a.amount);
  for (const r of envelopes) {
    const name = r.forKind === 'goal'
      ? state.goals.find((g) => g.id === r.forId)?.name ?? 'цель'
      : state.obligations.find((o) => o.id === r.forId)?.name
        ?? state.accounts.find((a) => a.id === r.forId)?.name
        ?? 'платёж';
    levers.push({
      id: `reserve:${r.id}`,
      title: `Взять из конверта «${name}»`,
      hint: `Отложено ${formatMoney(r.amount)}`,
      suggest: minM(r.amount, gap > ZERO ? gap : r.amount),
      max: r.amount,
      cost: `Деньги останутся на счёте, но «${name}» останется без покрытия на эту сумму`,
      cta: (v) => `Освободить ${formatMoney(v)} из конверта`,
      group: 'envelope',
      name,
      sub: `отложено ${formatMoney(r.amount)}`,
      apply: async (v) => {
        await setReserve({
          accountId: r.accountId, forKind: r.forKind, forId: r.forId,
          amount: clampPos(sub(r.amount, v)),
          ...(r.forDate ? { forDate: r.forDate } : {}),
        });
      },
    });
  }

  // 3. Взнос в цель: деньги свободны, но обещаны будущему. Пропуск взноса —
  //    единственный рычаг, который не берёт ничего у сегодняшнего дня; платит
  //    за него цель, и срок её надо назвать вслух.
  /*
    Ключ цикла для пропуска взноса.

    Не `savingsNudge`: тот отвечает на другой вопрос — «есть ли из чего
    откладывать», и без записанного прихода честно молчит. Здесь нужен
    ключ, которым помечается пропуск, и он обязан совпадать с циклом
    расчёта, иначе вычет взноса останется на месте.
  */
  const cycleStart = cycleStartFrom(
    state.incomeStreams, incomeFacts(state.transactions), todayPd(),
  );
  if (cycleStart) {
    for (const hold of holds) {
      const goal = state.goals.find((g) => g.id === hold.id);
      if (!goal) continue;
      levers.push({
        id: `goal:${goal.id}`,
        title: `Пропустить взнос в «${goal.name}»`,
        hint: `Взнос этого месяца — ${formatMoney(hold.amount)}`,
        suggest: hold.amount,
        max: hold.amount,
        fixed: true,
        cost: goalCost(goal),
        cta: (v) => `Пропустить взнос ${formatMoney(v)}`,
        group: 'goal',
        name: goal.name,
        sub: `взнос ${formatMoney(hold.amount)}`,
        apply: async () => { await skipGoalThisCycle(goal.id, cycleStart); },
      });
    }
  }

  const title = gap > ZERO ? `Как убрать нехватку ${formatMoney(gap)}` : 'Что можно сделать';

  if (picked === AWAIT) {
    const amount = parseMoney(raw, ZERO);
    const date = /^\d{4}-\d{2}-\d{2}$/.test(when) ? (when as PlainDate) : null;
    const outlook = date && amount > ZERO
      ? gapOutlook(gap, {
        id: 'draft', name: what, amount, amountBasis: 'known',
        expectedDate: date, dateBasis: 'confirmed', accountId: into?.id ?? '',
        status: 'planned', createdAt: '',
      }, needBy)
      : null;
    return (
      <SheetWrap title={title} onClose={onClose}>
        <div className="pick"><div className="k">Жду деньги, которых нет в плане</div></div>
        <div className="form">
          <label>Что за деньги
            <input value={what} onChange={(e) => setWhat(e.target.value)}
              placeholder="Вернут долг" /></label>
          <label>Сколько
            <input value={raw} onChange={(e) => setRaw(e.target.value)} inputMode="decimal"
              placeholder="5 000" /></label>
          <label>Когда ждёшь
            <DateButton label="Когда ждёшь" value={when} min={todayPd()} onChange={setWhen} /></label>
        </div>
        {/*
          Главное здесь — не то, что меняется, а то, что не меняется. Человек
          записывает обещанные деньги и вправе ждать, что цифра вырастет; если
          не сказать обратное прямо, он потратит то, чего нет.
        */}
        {outlook && (
          <div className="row"><div className="rl">Нехватка</div>
            <div className="rv num">
              {formatMoney(outlook.now)} → {formatMoney(outlook.after)} {dayMonth(outlook.on)}
            </div></div>
        )}
        <div className="note warn">
          Это запись на будущее, а не деньги: «Можно тратить» останется {formatMoney(available)},
          пока приход не случится. Придут — подтвердишь, и они станут деньгами.
        </div>
        {/*
          Срок — отдельный вопрос от денег. Обещанный возврат за куртку не
          должен становиться точкой, до которой растягивают остаток, а
          гонорар сдельщика — должен, и знает об этом только человек.
        */}
        <label className="remcheck">
          <input type="checkbox" checked={checkpoint}
            onChange={(e) => setCheckpoint(e.target.checked)} />
          <span>До этих денег нужно дожить</span>
          <Tip>{checkpoint
            ? `Если эти деньги придут раньше следующего дохода, «до прихода» будет считаться до ${date ? dayMonth(date) : 'их даты'}.`
            : 'Срок «до прихода» не изменится: ожидание просто будет ждать подтверждения.'}</Tip>
        </label>
        {outlook?.late && (
          <div className="note warn">
            Деньги приходят после {dayMonth(needBy)} — до этого дня нехватка остаётся.
          </div>
        )}
        {err && <div className="note warn">{err}</div>}
        <button className="btn pri" disabled={busy || amount <= ZERO || !date || !into}
          onClick={async () => {
            if (!date || !into) return;
            setBusy(true); setErr(null);
            try {
              await addExpectedIncome({
                name: what, amount, expectedDate: date, accountId: into.id,
                ...(checkpoint ? { horizonRole: 'checkpoint' as const } : {}),
              });
              flash(`Ждём ${formatMoney(amount)} — «${what.trim() || 'Приход'}», ${dayMonth(date)}`);
              onDone();
            } catch (e) {
              setErr(e instanceof Error ? e.message : 'Не получилось');
              setBusy(false);
            }
          }}>
          {busy ? 'Записываю…' : 'Записать ожидание'}
        </button>
        <button className="btn" onClick={() => setPicked(null)}>Назад к вариантам</button>
      </SheetWrap>
    );
  }

  if (picked === NOTHING) {
    const perDay = daysLeft > 0 ? (Math.round(gap / daysLeft) as Money) : gap;
    return (
      <SheetWrap title={title} onClose={onClose}>
        <div className="pick"><div className="k">Если ничего не менять
          <Tip>Обязательные платежи при этом остаются покрытыми: нехватка касается
            обычных трат, а не платежей.</Tip></div></div>
        <div className="note" style={{ marginTop: 0 }}>
          До следующего прихода не хватит {formatMoney(gap)}
          {daysLeft > 0 && <> — это {formatMoney(perDay)} в день, до прихода {plural(daysLeft, 'день', 'дня', 'дней')}</>}.
        </div>
        <button className="btn" onClick={() => setPicked(null)}>Назад к вариантам</button>
        <button className="btn pri" onClick={onClose}>Понятно, оставить как есть</button>
      </SheetWrap>
    );
  }

  if (picked) {
    const v = picked.fixed ? picked.suggest : parseMoney(raw, picked.suggest);
    const tooMuch = v > picked.max;
    const after = clampPos(sub(gap, v));
    return (
      <SheetWrap title={title} onClose={onClose}>
        <div className="pick"><div className="k">{picked.title}</div></div>
        {!picked.fixed && (
          <div className="form">
            <label>Сколько взять
              <input value={raw} onChange={(e) => setRaw(e.target.value)} inputMode="decimal"
                placeholder={String(picked.suggest / 100)} /></label>
          </div>
        )}
        {/* Предпросмотр: три числа, которые человек и держит в голове. */}
        <div className="row"><div className="rl">Свободно сейчас</div>
          <div className="rv num">{formatMoney(available)}</div></div>
        <div className="row"><div className="rl">Станет</div>
          <div className="rv num">{formatMoney((available + v) as Money)}</div></div>
        <div className="row"><div className="rl">Нехватка</div>
          <div className="rv num">
            {formatMoney(gap)} → {formatMoney(after)}
          </div></div>
        <div className="note warn">{picked.cost}</div>
        {tooMuch && (
          <div className="note warn">
            Больше {formatMoney(picked.max)} отсюда взять нельзя.
          </div>
        )}
        {err && <div className="note warn">{err}</div>}
        <button className="btn pri" disabled={busy || v <= ZERO || tooMuch}
          onClick={async () => {
            setBusy(true); setErr(null);
            try {
              await picked.apply(v);
              // Подтверждение после действия (04.11.2026): владелец не понимал, что произошло.
              flash(picked.group === 'own' ? `Переведено ${formatMoney(v)} с «${picked.name}»`
                : picked.group === 'envelope' ? `Освобождено ${formatMoney(v)} из «${picked.name}»`
                : `Взнос в «${picked.name}» пропущен`);
              onDone();
            } catch (e) {
              setErr(e instanceof Error ? e.message : 'Не получилось');
              setBusy(false);
            }
          }}>
          {busy ? 'Применяю…' : picked.cta(v)}
        </button>
        <button className="btn" onClick={() => setPicked(null)}>Назад к вариантам</button>
      </SheetWrap>
    );
  }

  /** Строка варианта: значок, имя, сумма подписью — как строки счетов. */
  const row = (key: string, icon: string, tone: string, name: string, sub: string, run: () => void) => (
    <button className="li w remrow" key={key} onClick={run}>
      <span className="picon" style={{ '--c': tone } as CSSProperties}><Icon name={icon} size={16} /></span>
      <div className="nm"><b>{name}</b><span>{sub}</span></div>
      <Icon name="chevron-right" size={13} className="ch" />
    </button>
  );
  const GROUPS = [
    { id: 'own', title: 'Перевести с накоплений', icon: 'piggy-bank', tone: 'var(--ok)' },
    { id: 'envelope', title: 'Взять из конверта', icon: 'envelope', tone: 'var(--ink-2)' },
    { id: 'goal', title: 'Пропустить взнос в цель', icon: 'pause', tone: 'var(--ink-2)' },
  ] as const;

  return (
    <SheetWrap title={title} onClose={onClose}>
      <div className="note" style={{ marginTop: 0 }}>
        Выбери, откуда взять деньги. Ничего не изменится, пока не подтвердишь.
      </div>
      {/* Сначала — будущие деньги: зарплата к сроку закрывает нехватку, ничего не забирая у сегодняшнего дня. */}
      {onPlanIncome && (
        <section className="blk remgrp">
          <div className="blkhead"><b>Из будущего прихода</b></div>
          {row('plan', 'calendar-check', 'var(--ok)', 'Назначить из ожидаемого прихода',
            'зарплата, аванс — деньги закрепятся, когда придут', onPlanIncome)}
        </section>
      )}
      {/* Варианты меняют разное: одни дают живые деньги, другие снимают
          назначение с уже лежащих. Поэтому — группами. */}
      {GROUPS.map((g) => {
        const list = levers.filter((l) => l.group === g.id);
        if (list.length === 0) return null;
        const shown = opened[g.id] ? list : list.slice(0, 3);
        return (
          <section className="blk remgrp" key={g.id}>
            <div className="blkhead"><b>{g.title}</b></div>
            {shown.map((l) => row(l.id, g.icon, g.tone, l.name, l.sub, () => { setPicked(l); setRaw(''); }))}
            {list.length > shown.length && (
              <button type="button" className="remmore" onClick={() => setOpened({ ...opened, [g.id]: true })}>
                Ещё {list.length - shown.length}</button>
            )}
          </section>
        );
      })}
      <section className="blk remgrp">
        <div className="blkhead"><b>Другое</b></div>
        {row('await', 'hourglass', 'var(--ink-2)', 'Жду деньги, которых нет в плане',
          'вернут долг, премия, продажа', () => { setPicked(AWAIT); setRaw(''); setWhen(''); })}
        {row('nothing', 'eye', 'var(--ink-2)', 'Ничего не менять', 'посмотреть, что будет', () => setPicked(NOTHING))}
      </section>
    </SheetWrap>
  );
}

/** Сумма из поля: пусто — берём предложенную. */
function parseMoney(raw: string, fallback: Money): Money {
  const clean = raw.replace(/[\s  ]/g, '').replace(',', '.');
  if (clean === '') return fallback;
  const n = Number(clean);
  if (!Number.isFinite(n) || n <= 0) return ZERO;
  return Math.round(n * 100) as Money;
}

/**
 * Чем платит цель за пропущенный взнос.
 *
 * Не «на месяц позже» наугад: срок считается тем же расчётом, что и в цели, —
 * при нынешнем взносе, от сегодня и от того же остатка. Пропуск сдвигает
 * начало накопления на цикл, и разница видна датами, а не обещанием.
 */
function goalCost(goal: AppState['goals'][number]): string {
  const now = goalEta(goal, goal.monthlyContribution, todayPd());
  const later = goalEta(goal, goal.monthlyContribution, addMonths(todayPd(), 1));
  if (!now || !later) return `«${goal.name}» пропустит взнос — срок закрытия отодвинется`;
  const late = goal.targetDate && isAfter(later, goal.targetDate);
  return `Цель закроется ${dayMonth(later)} вместо ${dayMonth(now)}`
    + (late ? ` — к сроку ${dayMonth(goal.targetDate ?? later)} уже не успеет` : '');
}

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** Дата словами: «14 марта 2027» — год у целей важен, они живут дольше месяца. */
function dayMonth(d: PlainDate): string {
  const [y, m, day] = d.split('-');
  return `${Number(day)} ${MONTHS[Number(m) - 1]} ${y}`;
}
